import { marked } from 'marked';

/**
 * Changelog content lives in src/assets/changelog/{lang}.md (one file per
 * supported UI language). Vite bundles each file as its own lazy raw-string
 * chunk, so only the active language's changelog is ever downloaded.
 *
 * Authoring contract (see src/assets/changelog/README.md):
 * - Every entry starts with `## dd/mm/yyyy - vX.Y.Z - Title`.
 * - Internal app links are written as `[label]({store}/path)`.
 */
const CHANGELOG_FILES = import.meta.glob<string>(
  ['../../assets/changelog/*.md', '!**/README.md'],
  { query: '?raw', import: 'default' }
);

const FALLBACK_LANG = 'en';

export interface IChangelogHeadline {
  day: string;
  month: string;
  title: string;
}

const markdownCache = new Map<string, Promise<string>>();

function globKeyFor(lang: string): string | null {
  const key = `../../assets/changelog/${lang}.md`;
  return key in CHANGELOG_FILES ? key : null;
}

/**
 * Loads the raw changelog markdown for a language, falling back to English
 * when that language has no file. Results are cached per language.
 */
export function loadChangelogMarkdown(lang: string): Promise<string> {
  const key = globKeyFor(lang) ?? globKeyFor(FALLBACK_LANG);
  if (!key) {
    return Promise.reject(new Error('No changelog files bundled'));
  }
  let cached = markdownCache.get(key);
  if (!cached) {
    cached = CHANGELOG_FILES[key]();
    markdownCache.set(key, cached);
    // Don't cache failures (e.g. a transient offline chunk-load error), so
    // reopening the modal can recover without a page reload.
    cached.catch(() => markdownCache.delete(key));
  }
  return cached;
}

const HEADLINE_REGEX = /^## (\d{2})\/(\d{2})\/\d{4} - v[\d.]+ - (.+)$/m;

/** Parses the first `## dd/mm/yyyy - vX.Y.Z - Title` heading found. */
export function parseLatestHeadline(md: string): IChangelogHeadline | null {
  const match = HEADLINE_REGEX.exec(md);
  if (!match) return null;
  return { day: match[1], month: match[2], title: match[3].trim() };
}

/** "dd/mm - Title" label for the dashboard news button, null if unparsable. */
export function newsButtonLabel(md: string): string | null {
  const headline = parseLatestHeadline(md);
  if (!headline) return null;
  return `${headline.day}/${headline.month} - ${headline.title}`;
}

/**
 * Renders changelog markdown to HTML, resolving `{store}` link placeholders
 * to the restaurant's base path (e.g. "/bobas"). When no base path is
 * available (modal opened outside a restaurant context), internal links
 * degrade to plain text — there is no valid route to point them at.
 * Content is repo-authored and PR-reviewed (same trust level as the
 * previous inline HTML template), so no sanitizer is applied.
 */
export function renderChangelogHtml(md: string, storeBasePath: string): string {
  const resolved = storeBasePath
    ? md.replaceAll('{store}', storeBasePath)
    : md.replace(/\[([^\]]+)\]\(\{store\}[^)]*\)/g, '$1');
  return marked.parse(resolved, { async: false, gfm: true, breaks: true });
}
